import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { WorkflowService } from '../../../../../../../../shared/services/workflow.service';
import { Subject } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { allBidItems, changeInWorkCodeOptions, changeInWorkRequest, projectDetails } from '../../../../../../core/projects.selectors';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import {
  ClearChangeInWorkRequestWithId,
  FetchAllBidItemsRequest,
  FetchBasisOfPayOptionsRequest,
  FetchChangeInWorkCodeOptionsRequest,
  FetchChangeInWorkRequestWithIdRequest,
  SaveChangeInWorkRequestRequest,
} from '../../../../../../core/projects.actions';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../state/app.state';
import { ExpressionEvaluatorService } from '../../../../../../../../shared/services/expression-evaluator.service';
import { ProjectsService } from '../../../../../../core/projects.service';
import { ProjectDetailsService } from '../../project-details.service';
import { GetVideoIdRequest, UploadFileRequest } from '../../../../../../../../shared/core/shared.actions';
import { classifyFile } from 'src/app/shared/helpers';
import { ActivatedRoute, Router } from '@angular/router';
import { Account } from '../../../../../../../../shared/models';
import { AuthService } from '../../../../../../../../shared/services';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ChangeInWorkRequestReportComponent } from '../change-in-work-request-report/change-in-work-request-report.component';
import { AnalyticsService } from '../../../../../../../../shared/services/analytics.service';
import { isUnitPriceLocked } from '../change-in-work-request-line-item/unit-price-rules';

@Component({
    selector: 'app-create-edit-change-in-work-request-form',
    templateUrl: './create-edit-change-in-work-request-form.component.html',
    styleUrls: ['./create-edit-change-in-work-request-form.component.scss'],
    standalone: false
})
export class CreateEditChangeInWorkRequestFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('fileInput', { static: false }) fileInputRef: any;
  @ViewChild('displayErrorRef') displayErrorElement: ElementRef;
  @Input() editChangeInWorkRequestForm = false;
  data: any;
  lineErrors: Record<string, boolean> = {};
  limits: any;
  bidItemsList = [];
  filteredBidItemsList = [];
  pendingBidItems = [];
  lineSourceRequests: Record<string, string> = {};
  focusedIn = false;
  searchText: string;
  errorMessages: any = {};
  errorMessageMap = {
    overAllNotes: {
      required: 'This field is required.',
    },
    fundingSource: {
      required: 'This field is required.',
    },
    delegatedLimit: {
      required: 'This field is required.',
    },
    changeInWorkCode: {
      required: 'This field is required.',
    },
    requestNo: {
      required: 'This field is required.',
    },
    orderNo: {
      required: 'This field is required.',
    },
    subContractor: {
      required: 'This field is required.',
    },
    pictures: {
      required: 'This field is required.',
    },
    videos: {
      required: 'This field is required.',
    },
  };
  form = new FormGroup<any>({
    requestNo: new FormControl({ value: null, disabled: false }),
    orderNo: new FormControl({ value: null, disabled: false }),
    subContractor: new FormControl({ value: null, disabled: true }),
    overAllNotes: new FormControl({ value: null, disabled: false }),
    fundingSource: new FormControl({ value: null, disabled: false }),
    delegatedLimit: new FormControl({ value: null, disabled: false }),
    changeInWorkCode: new FormControl({ value: null, disabled: false }),
    totalAdditionalDays: new FormControl({ value: 0, disabled: false }),
    pictures: new FormControl({ value: '[]', disabled: false }),
    videos: new FormControl({ value: '[]', disabled: false }),
  });
  lineKeys: any[] = [];
  selectedLineId: any;
  totalAmount = 0;
  totalNewWorkingDays = 0;
  basisOfPayOptions = [];
  changeInWorkCodeOptions = [];
  project: any;
  subContractorOptions = [];
  mediaFiles = [];
  videoToBeDeletedFromMuxDb = [];
  isFormDirty = false;
  editable = true;
  addMenuOptions = [
    {
      key: 'picture',
      icon: 'paperclip',
      label: 'Attachments',
      handler: () => {
        this.fileInputRef.nativeElement.click();
      },
    },
  ];
  actions = [];
  creator = false;
  account: Account;
  displayError = false;
  changeOrderCreateMode = 'manual';
  totalAmountArray = [];
  totalAdditionalWorkingDaysArray = [];
  // Guards against an out-of-order pending-bid-items response overwriting a
  // newer one — see loadPendingBidItems()
  private pendingFetchSeq = 0;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private workflowService: WorkflowService,
    private commonService: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private projectsService: ProjectsService,
    private projectDetailsService: ProjectDetailsService,
    private cdRef: ChangeDetectorRef,
    private evaluator: ExpressionEvaluatorService,
    public authService: AuthService,
    public dialog: MatDialog,
    private analyticsService: AnalyticsService,
  ) {

    this.route.queryParams
      .pipe(
        map(qp => qp?.ciwr || null),
        distinctUntilChanged(),
        takeUntil(this.onDestroy),
      )
      .subscribe(ciwr => {
        if (ciwr) {
          this.store.dispatch(FetchChangeInWorkRequestWithIdRequest({ payload: { changeInWorkRequestId: ciwr } }));
        } else {
          this.store.dispatch(ClearChangeInWorkRequestWithId());
        }
      });
  }

  ngOnInit() {
    this.account = this.authService.getCurrentUser();

    this.projectsService.totalAmount
      .pipe(debounceTime(200), takeUntil(this.onDestroy))
      .subscribe(() => {
        let total = 0;

        Object.keys(this.form.value).forEach((key) => {
          if (key.startsWith('line_')) {
            const lineValue = this.form.value[key];
            if (lineValue && typeof lineValue.totalAmount === 'number') {
              total += lineValue.totalAmount;
            }
          }
        });

        this.totalAmount = total;
      });

    this.form.controls.totalAdditionalDays.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((val) => {
        this.totalNewWorkingDays = parseInt(val, 10) || 0;
      });

    this.projectsService.isAnyFormDirty
      .pipe(takeUntil(this.onDestroy))
      .subscribe((dirty) => {
        this.isFormDirty = dirty;
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.project = project;
        if (project && Object.keys(project).length) {
          this.changeOrderCreateMode = project?.source_entity_extra_attributes?.change_order_create_mode || 'manual';
        }
        const lookupPayload = {
          entityId: project?.owner_organization?.id,
          entityType: 'organization',
        };

        this.subContractorOptions = project.sub_contractors
          .filter(o => o.prime)
          .map(o => {
            return {
              ...o,
              value: o.id,
              label: o.name,
            };
          });

        setTimeout(() => {
          this.form.patchValue({
            subContractor: this.subContractorOptions?.[0]?.value,
            delegatedLimit: project?.['source_entity_extra_attributes']?.['change_order_delegated_limit'],
          }, { emitEvent: false });
        });

        this.store.dispatch(FetchAllBidItemsRequest({
          payload: {
            projectId: project?.id,
            qp: {
              include: [
                {
                  bid_items: ['fields'],
                },
              ],
            },
          },
        }));

        this.store.dispatch(FetchBasisOfPayOptionsRequest({
          payload: lookupPayload,
        }));

        this.store.dispatch(FetchChangeInWorkCodeOptionsRequest({
          payload: lookupPayload,
        }));

        // An existing request re-fetches with its own items excluded once it
        // has loaded, so only a brand-new request needs to fetch here
        if (!this.data?.id) {
          this.loadPendingBidItems();
        }
      });

    this.store.select(allBidItems)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((bidItems) => {
        this.bidItemsList = bidItems;
        this.backfillExistingQuantities();
        this.refreshNewItemFlags();
      });

    this.store.select(changeInWorkCodeOptions)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.changeInWorkCodeOptions = data;
      });

    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        const items = Array.isArray(data) ? data : [data];

        items.forEach((eachItem: any) => {
          if (eachItem?.['fileType'] === 'picture' || eachItem?.['fileType'] === 'document') {
            let pictures: any = this.form.get('pictures')?.value || '[]';
            pictures = JSON.parse(pictures);

            pictures.push(eachItem);
            this.form?.get('pictures')?.setValue(JSON.stringify(pictures), { emitEvent: true });
            this.mediaFiles.push(eachItem);
          } else if (eachItem?.['fileType'] === 'video') {
            let videos: any = this.form.get('videos')?.value || '[]';
            videos = JSON.parse(videos);

            videos.push(eachItem);
            this.form
              ?.get('videos')
              ?.setValue(JSON.stringify(videos), { emitEvent: true });
            this.mediaFiles.push(eachItem);
          }
        });

        this.mediaFiles = [...this.mediaFiles];
      });

    this.form.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.projectsService.isAnyFormDirty.next(true);
      });

    this.projectsService.addValueToParentForm
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.form.controls?.[data?.lineKey]?.patchValue({
          ...data.value,
        }, { emitEvent: false }); // ⚠ prevents loop

        // The item number the child just wrote decides the "New item" badge
        this.refreshNewItemFlags(data?.lineKey);
      });

    this.projectDetailsService.triggerSave
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.save();
      });

    this.store.select(changeInWorkRequest)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.data = {};
        this.lineKeys = [];
        this.mediaFiles = [];
        this.totalAmountArray = [];
        this.totalAdditionalWorkingDaysArray = [];
        this.lineSourceRequests = {};

        this.data = data;

        // Editable only if no status (new) or draft/rejected (can resubmit)
        // Submitted/approved CORs are read-only — workflow actions handle approve/reject
        this.editable = !this.data?.status || this.data?.status === 'draft' || this.data?.status === 'rejected';

        Object.keys(this.form.controls)
          .filter(key => key.startsWith('line_'))
          .forEach(key => this.form.removeControl(key, { emitEvent: false }));

        if (this.data?.id) {
          this.form.patchValue({
            requestNo: this.data?.request_no,
            orderNo: this.data?.change_order_number,
            subContractor: this.data?.sub_contractor?.id,
            fundingSource: this.data?.funding_source,
            delegatedLimit: this.data?.delegated_limit,
            changeInWorkCode: this.data?.change_in_work_code,
            overAllNotes: this.data?.description,
            totalAdditionalDays: this.data?.total_additional_days || 0,
            pictures: JSON.stringify(this.data?.pictures),
            videos: JSON.stringify(this.data?.videos),
          }, { emitEvent: false });

          this.mediaFiles.push(...(this.data?.media || []));

          this.data?.lines.forEach((o, i) => {
            this.totalAdditionalWorkingDaysArray.push(Number(o?.additional_working_days) || 0);

            let result = 0;
            if (o?.original_unit_price != null) {
              // Repricing snapshot: new_price * (orig + delta) - orig_price * orig
              const newPrice = Number(o?.unit_price) || 0;
              const deltaQty = Number(o?.quantity) || 0;
              const origQty = Number(o?.original_quantity) || 0;
              const origPrice = Number(o?.original_unit_price) || 0;
              result = newPrice * (origQty + deltaQty) - origPrice * origQty;
            } else if (o?.unit_price && o?.quantity) {
              result = (Number(o.unit_price) || 0) * (Number(o.quantity) || 0);
            }
            this.totalAmountArray.push(result);

            const lineKey = `line_${i + 1}`;

            const newItemGroup = new FormGroup({
              searchText: new FormControl({ value: '', disabled: false }),
              itemNo: new FormControl({ value: o?.item_no, disabled: true }, { validators: [Validators.required] }),
              id: new FormControl({ value: o?.id, disabled: false }),
              item: new FormControl({ value: `${o?.item_no} ${o?.item_description}`, disabled: false }),
              uom: new FormControl({ value: o?.unit, disabled: false }, { validators: [Validators.required] }),
              desc: new FormControl({ value: o?.item_description, disabled: false }, { validators: [Validators.required] }),
              basisOfPay: new FormControl({ value: o?.basis_of_pay, disabled: false }, { validators: [Validators.required] }),
              // Existing bid item quantity — drives the "Change In Quantity"
              // label + hint on lines that modify an existing item. Prefer the
              // line's as-written snapshot (stable after CO approval); fall
              // back to the live bid item, backfilled once allBidItems arrives
              // (see backfillExistingQuantities)
              quantity: new FormControl({
                value: o?.original_quantity ?? this.findExistingBidItemQuantity(o?.item_no),
                disabled: false,
              }),
              originalUnitPrice: new FormControl({ value: o?.original_unit_price ?? null, disabled: false }),
              changeInQuantity: new FormControl({ value: o?.quantity, disabled: false }, { validators: [Validators.required] }),
              unitPrice: new FormControl({ value: o?.unit_price, disabled: false }, { validators: [Validators.required] }),
              totalAmount: new FormControl({ value: result, disabled: false }, { validators: [Validators.required] }),
              additionalWorkingDays: new FormControl({
                value: o?.additional_working_days,
                disabled: false,
              }),
              locationOfChange: new FormControl({ value: o?.location, disabled: false }),
              bidItemId: new FormControl({ value: o?.bid_item?.id ?? null, disabled: false }),
            });

            this.form.addControl(lineKey, newItemGroup, { emitEvent: false });
          });

          this.lineKeys = this.getLineKeys() || [];

          this.refreshNewItemFlags();

          this.totalAmount = this.totalAmountArray.reduce((sum, val) => sum + val, 0);
          this.totalNewWorkingDays = this.totalAdditionalWorkingDaysArray.reduce((sum, val) => sum + val, 0);

          this.actions = this.workflowService.canExecute() ? (this.data?.['next-actions']?.[0]?.actions || []) : [];

          if (this.actions?.length && this.data.createdBy && Object.keys(this.data.createdBy).length > 0) {
            const crtBy = `${this.data.createdBy.first_name} ${this.data.createdBy.last_name}`;
            this.creator = crtBy === this.account.name;
          }

          // Disable all form fields when not editable (submitted/approved)
          if (!this.editable) {
            this.form.disable({ emitEvent: false });
          } else {
            this.form.enable({ emitEvent: false });
          }

          // Re-fetch now that the request id is known, so its own items are
          // excluded from the pending suggestions
          this.loadPendingBidItems();
        }
      });
  }

  ngAfterViewInit() {
    this.projectsService.isAnyFormDirty.next(false);
  }

  /**
   * New bid items introduced by other pending change order requests — offered
   * in the Add Item picker as "New item" entries. This request's own lines are
   * excluded so its items don't come back as suggestions (or trip the
   * duplicate-item-number validator) against itself.
   */
  private loadPendingBidItems() {
    if (!this.project?.id) {
      return;
    }
    // Two fetches can be in flight (project loads before the request does), and
    // the un-excluded one must never win — its rows include this request's own
    // items, which would trip the line item's duplicate-item-number validator
    const seq = ++this.pendingFetchSeq;
    this.projectsService.fetchPendingBidItems({ projectId: this.project.id, exclude: this.data?.id })
      .pipe(takeUntil(this.onDestroy))
      .subscribe(response => {
        if (seq !== this.pendingFetchSeq) {
          return;
        }
        this.pendingBidItems = response?.data || [];
      });
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  addItem() {
    this.displayError = false;
    let index = 1;
    let lineKey = `line_${index}`;
    while (this.form.contains(lineKey)) {
      index++;
      lineKey = `line_${index}`;
    }

    const newItemGroup = new FormGroup({
      searchText: new FormControl({ value: '', disabled: false }),
      itemNo: new FormControl({ value: '', disabled: true }, {
        validators: [Validators.required],
      }),
      item: new FormControl({ value: '', disabled: false }),
      uom: new FormControl({ value: null, disabled: false }, {
        validators: [Validators.required],
      }),
      desc: new FormControl({ value: null, disabled: false }, {
        validators: [Validators.required],
      }),
      basisOfPay: new FormControl({ value: null, disabled: false }, {
        validators: [Validators.required],
      }),
      quantity: new FormControl({ value: null, disabled: false }),
      originalUnitPrice: new FormControl({ value: null, disabled: false }),
      changeInQuantity: new FormControl({ value: null, disabled: false }, {
        validators: [Validators.required],
      }),
      unitPrice: new FormControl({ value: null, disabled: false }, {
        validators: [Validators.required],
      }),
      totalAmount: new FormControl({ value: null, disabled: false }, {
        validators: [Validators.required],
      }),
      additionalWorkingDays: new FormControl({ value: null, disabled: false }),
      locationOfChange: new FormControl({ value: null, disabled: false }),
      bidItemId: new FormControl({ value: null, disabled: false }),
    });

    this.form.addControl(lineKey, newItemGroup, { emitEvent: true });

    this.lineKeys = this.getLineKeys() || [];

    this.refreshNewItemFlags(lineKey);

    this.selectLine(lineKey);
  }

  getLineKeys() {
    this.lineKeys = [];
    return Object.keys(this.form.controls || {})
      .filter(key => key.startsWith('line_'));
  }

  selectLine(key: any) {
    this.searchText = null;
    this.filteredBidItemsList = this.bidItemsList;
    this.selectedLineId = this.selectedLineId === key ? null : key;
    this.projectsService.isAnyFormDirty.next(false);
  }

  deleteLine(key: any) {
    this.form.removeControl(key, { emitEvent: true });
    this.lineKeys = this.lineKeys.filter(o => o !== key);
    this.selectedLineId = null;

    this.totalAmountArray = [];
    this.totalAdditionalWorkingDaysArray = [];

    const formValues = this.form.getRawValue();

    Object.keys(formValues).forEach(k => {
      if (k.startsWith('line_')) {
        const line = formValues[k];
        if (line) {
          this.totalAmountArray.push(Number(line?.totalAmount || 0));
          this.totalAdditionalWorkingDaysArray.push(Number(line?.additionalWorkingDays || 0));
        }
      }
    });

    this.totalAmount = this.totalAmountArray.reduce((sum, val) => sum + val, 0);
    this.totalNewWorkingDays = this.totalAdditionalWorkingDaysArray.reduce((sum, val) => sum + val, 0);

    this.cdRef.detectChanges();
  }

  deletePicture(pic) {
    if (pic.fileType === 'picture' || pic.fileType === 'document') {
      let pictures = this.getPictures();

      pictures = pictures?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('pictures')?.setValue(JSON.stringify(pictures));
    }

    if (pic.fileType === 'video') {
      let videos = this.getVideos();

      videos = videos?.filter(o => o.id !== pic.id);
      this.mediaFiles = this.mediaFiles?.filter(o => o.id !== pic.id);
      this.form?.get('videos')?.setValue(JSON.stringify(videos));
      this.videoToBeDeletedFromMuxDb.push(pic.id);
    }
  }

  getPictures() {
    const pictures = this.form.controls['pictures']?.value || '[]';
    return JSON.parse(pictures);
  }

  getVideos() {
    const videos = this.form.controls['videos']?.value || '[]';
    return JSON.parse(videos);
  }

  save() {
    this.displayError = false;

    if (this.form.invalid) {
      this.projectsService.checkForErrors.next(true);
      this.validateLineItems();
      this.checkForErrors();
      return;
    }

    this.selectedLineId = null;

    const formData = this.form.getRawValue();

    const lineEntries = Object.entries(formData).filter(([key, _]) =>
      key.startsWith('line_'),
    );

    const headerDays = parseInt(formData?.totalAdditionalDays, 10) || 0;
    if (!lineEntries.length && headerDays === 0) {
      this.displayError = true;
      setTimeout(() => {
        this.displayErrorElement?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }

    let payload: any = {
      id: this.data?.id ?? null,
      request_no: formData?.requestNo,
      change_order_number: formData?.orderNo,
      project_id: this.project?.id,
      sub_contractor_id: formData?.subContractor,
      description: formData?.overAllNotes,
      funding_source: formData?.fundingSource,
      delegated_limit: formData?.delegatedLimit,
      change_in_work_code: formData?.changeInWorkCode,
      total_additional_days: parseInt(formData?.totalAdditionalDays, 10) || 0,
      pictures: JSON.parse(formData?.pictures),
      videos: JSON.parse(formData?.videos),
    };

    payload = this.manageMedia(payload);

    const lines = [];

    Object.entries(formData)
      .filter(([lineKey, _]: any) => lineKey.startsWith('line_'))
      .forEach(([_, lineKeyValue]: any) => {
        const matchedItem = this.bidItemsList.find(
          o => String(o.item).trim() === String(lineKeyValue?.itemNo).trim(),
        );
        lines.push({
          item_no: lineKeyValue?.itemNo,
          item_description: lineKeyValue?.desc,
          quantity: lineKeyValue?.changeInQuantity,
          unit_price: lineKeyValue?.unitPrice,
          unit: lineKeyValue?.uom,
          bid_item_id: lineKeyValue?.bidItemId || matchedItem?.id || null,
          additional_working_days: lineKeyValue?.additionalWorkingDays,
          location: lineKeyValue?.locationOfChange,
          basis_of_pay: lineKeyValue?.basisOfPay,
          id: lineKeyValue?.id || null,
        });
      });

    payload.lines = lines;

    const temp = {
      type: 'save',
      payload,
    };

    this.store.dispatch(SaveChangeInWorkRequestRequest({ payload: temp }));

    if (!this.data?.id) {
      this.analyticsService.capture('change_order_action', {
        action: 'ciwr_created',
        project_id: this.project?.id,
      });
    }
  }

  submit() {
    this.displayError = false;

    // For non-editable states (submitted/approved), skip validation and go straight to report
    if (!this.editable) {
      this.router.navigate([], {
        queryParams: { snSubTab: 'workflow' },
        queryParamsHandling: 'merge',
      }).then(() => {
        this.dialog.open(ChangeInWorkRequestReportComponent, {
          width: '1000px',
          height: '1100px',
          data: { ...this.data },
        });
      });
      return;
    }

    if (this.form.invalid) {
      this.projectsService.checkForErrors.next(true);
      this.validateLineItems();
      this.checkForErrors();
      return;
    }

    this.selectedLineId = null;

    const formData = this.form.getRawValue();

    const lineEntries = Object.entries(formData).filter(([key, _]) =>
      key.startsWith('line_'),
    );

    const headerDays = parseInt(formData?.totalAdditionalDays, 10) || 0;
    if (!lineEntries.length && headerDays === 0) {
      this.displayError = true;
      setTimeout(() => {
        this.displayErrorElement?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }

    let payload: any = {
      id: this.data?.id ?? null,
      request_no: formData?.requestNo,
      change_order_number: formData?.orderNo,
      project_id: this.project?.id,
      sub_contractor_id: formData?.subContractor,
      description: formData?.overAllNotes,
      funding_source: formData?.fundingSource,
      delegated_limit: formData?.delegatedLimit,
      change_in_work_code: formData?.changeInWorkCode,
      total_additional_days: parseInt(formData?.totalAdditionalDays, 10) || 0,
      pictures: JSON.parse(formData?.pictures),
      videos: JSON.parse(formData?.videos),
    };

    payload = this.manageMedia(payload);

    const lines = [];

    Object.entries(formData)
      .filter(([lineKey, _]: any) => lineKey.startsWith('line_'))
      .forEach(([_, lineKeyValue]: any) => {
        const matchedItem = this.bidItemsList.find(
          o => String(o.item).trim() === String(lineKeyValue?.itemNo).trim(),
        );
        lines.push({
          item_no: lineKeyValue?.itemNo,
          item_description: lineKeyValue?.desc,
          quantity: lineKeyValue?.changeInQuantity,
          unit_price: lineKeyValue?.unitPrice,
          unit: lineKeyValue?.uom,
          bid_item_id: lineKeyValue?.bidItemId || matchedItem?.id || null,
          additional_working_days: lineKeyValue?.additionalWorkingDays,
          location: lineKeyValue?.locationOfChange,
          basis_of_pay: lineKeyValue?.basisOfPay,
          id: lineKeyValue?.id || null,
          // comment: lineKeyValue?.comment,
        });
      });

    payload.lines = lines;

    const temp = {
      type: 'submit',
      payload,
    };

    if (this.data.status === 'submitted') {
      this.router.navigate([], {
        queryParams: {
          snSubTab: 'workflow',
        },
        queryParamsHandling: 'merge',
      }).then(() => {
        this.dialog.open(ChangeInWorkRequestReportComponent, {
          width: '1000px',
          height: '1100px',
          data: {
            ...this.data,
          },
        });
      });
    } else {
      this.store.dispatch(SaveChangeInWorkRequestRequest({ payload: temp }));
    }
  }

  manageMedia(data) {
    data.pictures = data.pictures?.map(o => o.id);
    data.videos = data.videos?.map(o => o.id);
    return data;
  }

  onFileSelected(event: any) {
    const files: FileList = event.target.files;  // Get selected files
    const imageFormData = new FormData();
    const videoFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);

      if (classifyFile(file) === 'video') {
        videoFiles.push(file);
      } else {
        imageFormData.append('inputFiles', file);
      }
    }

    if (imageFormData.has('inputFiles')) {
      this.store.dispatch(UploadFileRequest({ payload: imageFormData }));
    }

    if (videoFiles.length > 0) {
      videoFiles.forEach((videoFile: File) => {
        const videoElement = document.createElement('video');
        const reader = new FileReader();

        reader.onloadend = () => {
          videoElement.src = reader.result as string;

          videoElement.onloadedmetadata = () => {
            const videoDuration = videoElement.duration;
            const maxDuration = this.limits.video_size_in_secs;

            if (videoDuration > maxDuration) {
              this.commonService.notification(
                `Video duration is ${videoDuration} seconds, should be less than ${maxDuration} seconds.`,
                'success',
              );
            } else {
              this.store.dispatch(GetVideoIdRequest({ payload: videoFile }));
            }
          };
        };

        reader.readAsDataURL(videoFile);
      });
    }

    this.fileInputRef.nativeElement.value = '';
  }

  /**
   * Current bid-sheet quantity for a line's item number, or null when the
   * line doesn't reference an existing bid item (new/pending items).
   */
  private findExistingBidItemQuantity(itemNo: any) {
    if (!itemNo) {
      return null;
    }
    const matched = this.bidItemsList?.find(
      o => String(o?.item ?? '').trim() === String(itemNo).trim(),
    );
    return matched?.quantity ?? null;
  }

  /**
   * Saved lines are loaded before the bid items may have arrived; fill the
   * existing-quantity control in once they do so the line renders as
   * "Change In Quantity" with the existing-quantity hint rather than looking
   * like a brand-new item.
   */
  private backfillExistingQuantities() {
    if (!this.bidItemsList?.length) {
      return;
    }
    (this.lineKeys || []).forEach(lineKey => {
      const group = this.form.get(lineKey) as FormGroup;
      const quantityCtrl = group?.get('quantity');
      if (!quantityCtrl || quantityCtrl.value != null) {
        return;
      }
      const existingQty = this.findExistingBidItemQuantity(group.get('itemNo')?.value);
      if (existingQty != null) {
        quantityCtrl.setValue(existingQty, { emitEvent: false });
      }
    });
  }

  /**
   * Flags each line that introduces an item the bid sheet doesn't carry yet, so
   * the accordion header can badge it "New item". Recomputed whenever the bid
   * items arrive, since saved lines load before the bid sheet does. Pass a
   * lineKey to refresh just that line — the child-form bridge does this on every
   * keystroke, so the badge tracks the item number as it is typed.
   */
  private refreshNewItemFlags(lineKey?: string) {
    const keys = lineKey ? [lineKey] : (this.lineKeys || []);
    keys.forEach(key => {
      const group = this.form.get(key) as FormGroup;
      if (!group) {
        return;
      }
      const itemNo = group.get('itemNo')?.value;
      const isNew = !!itemNo
        && !isUnitPriceLocked(itemNo, group.get('bidItemId')?.value, this.bidItemsList);
      this.lineSourceRequests[key] = isNew ? 'new' : null;
    });
  }

  validateLineItems() {
    this.lineErrors = {};
    const invalidLineKeys: string[] = [];

    this.lineKeys.forEach(lineKey => {
      const lineGroup = this.form.get(lineKey) as FormGroup;
      let hasError = false;

      Object.entries(lineGroup.controls).forEach(([controlName, control]) => {
        if (['searchText', 'quantity', 'originalUnitPrice', 'locationOfChange', 'bidItemId'].includes(controlName)) {
          return;
        }

        if (!control.value || control.value === '') {
          hasError = true;
        }

        // Also surface validator failures (e.g. duplicate item number)
        if (control.enabled && control.invalid) {
          hasError = true;
        }
      });

      this.lineErrors[lineKey] = hasError;

      if (hasError) {
        invalidLineKeys.push(lineKey);
      }
    });

    if (invalidLineKeys.length > 0) {
      this.selectedLineId = invalidLineKeys[0];
      this.cdRef.detectChanges();

      setTimeout(() => {
        const el = document.getElementById(this.selectedLineId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);
    }
  }

  getLineGroup(lineKey: string): FormGroup {
    return this.form.get(lineKey) as FormGroup;
  }

  isEllipsisActive(el: HTMLElement): boolean {
    return el ? el.scrollWidth > el.clientWidth : false;
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
